import { diffChars } from "diff";
import { flatten } from "lodash";
import { useEffect, useState } from "react";
import {
  getBoundaryStyleDeltas,
  getStyleSplitRangeDeltas,
  getStyleSplitUndoRangeDeltas,
  isHomogenous,
  isOutlookLineChange,
} from "../richContent/richMutationRecords";
import { getPath } from "../writeshift/Path";
import { useAppDispatch } from "../store/hooks";
import {
  NodeAddition,
  NodeRemoval,
  RichEdit,
  punt,
  richEdits,
  styleEdits,
} from "../contents/underlines/writingAlertsSlice";
import usePrevious from "../contents/underlines/hooks/usePrevious";

// TODO: replace the mutation observer with the mutation summary: https://github.com/mmacfadden/mutation-summary
export default function useDispatchRichEdits(
  root: HTMLElement,
  editorId: number
): void {
  const dispatch = useAppDispatch();

  // TODO(refactor): this should be useTextContent or somethign like that
  const [textContent, setTextContent] = useState(root.textContent);
  const previousTextContent = usePrevious(textContent);
  useEffect(() => {
    const observer = new MutationObserver((recordList) => {
      setTextContent(root.textContent);
      // Some rich content edits leave text nodes next to each other, which
      // messes up the concept of paths (because paths are assumed to describe
      // normalized html dom trees)
      root.normalize();

      const records = recordList;

      const isStyleChange = previousTextContent === root.textContent;
      if (isStyleChange) {
        const deltas =
          getStyleSplitRangeDeltas(root, records) ||
          getStyleSplitUndoRangeDeltas(root, records) ||
          getBoundaryStyleDeltas(root, records);
        if (deltas) {
          dispatch(
            styleEdits({
              editorId,
              styleEdits: deltas,
            })
          );
          return;
        }
      }

      const isLineChange = isOutlookLineChange(records);

      // We don't recognize this sequence of edits,
      // so remove all the underlines and
      // wait to do a full refresh.
      if (records.length > 1 && !isHomogenous(records) && !isLineChange) {
        dispatch(punt({ editorId, records }));
        return;
      }

      const edits: RichEdit[][] = records.map((record) => {
        switch (record.type) {
          case "childList": {
            const added = Array.from(record.addedNodes);
            const addedEdits = added.map((a) => {
              const addedPath = getPath({ root, target: a });
              return {
                addedPath,
              };
            });

            const removed = Array.from(record.removedNodes);
            const removedEdits = removed.map((r) => {
              const removedPath = getPath({ root, target: r });
              return {
                removedPath,
              };
            });
            const allEdits: RichEdit[] = [];
            for (const addedEdit of addedEdits) {
              if (addedEdit.addedPath !== null) {
                // Type Safety: we just checked not null,
                // idk what TS is doing here :(
                allEdits.push(addedEdit as NodeAddition);
              }
            }
            for (const removedEdit of removedEdits) {
              if (removedEdit.removedPath !== null) {
                // Type safety: null check above
                allEdits.push(removedEdit as NodeRemoval);
              }
            }
            return allEdits;
          }
          case "characterData": {
            // traditional diff, amend the start if we need to
            // and remove alerts that touch the boundary of an edit
            const previous = record.oldValue;
            const text = record.target.textContent;
            const textEdits = diffChars(previous || "", text || "");
            const nodePath = getPath({ root, target: record.target });
            return nodePath
              ? [
                  {
                    textEdits,
                    nodePath,
                  },
                ]
              : [];
          }
          default:
            return [];
        }
      });
      const flattenedEdits = flatten(edits);
      dispatch(
        richEdits({
          richEdits: flattenedEdits,
          editorId,
        })
      );
    });

    observer.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      characterDataOldValue: true,
    });
    return () => {
      observer.disconnect();
    };
  });
}
